import {
  Grid,
  Card,
  Box,
  Stack,
  Chip,
  InputBase,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import MultiPlacePicker from "components/MDPlacePicker/MultiPlacePicker";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useMaterialUIController } from "context";
import { getPlaces } from "store/slice/places/placeSlice";
import {
  getFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
  assignFaqPlaces,
  reorderFaqs,
} from "store/slice/faq/faqSlice";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import MDDialog from "components/MDDialog";

// Syncs Formik placeIds with Redux multiSelectedPlaces
function FormikPlacesSync({ selectedIds }) {
  const { setFieldValue, values } = useFormikContext();
  useEffect(() => {
    const current = Array.isArray(values.placeIds) ? values.placeIds : [];
    const next = Array.isArray(selectedIds) ? selectedIds : [];
    if (current.length !== next.length || current.some((id) => !next.includes(id))) {
      setFieldValue("placeIds", next, false);
    }
  }, [selectedIds, setFieldValue, values.placeIds]);
  return null;
}

export default function Faq() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const dispatch = useDispatch();
  
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "", placeIds: [] });
  const [placeSelectLocked, setPlaceSelectLocked] = useState(false);
  const [placeFilter, setPlaceFilter] = useState("");

  const { items: faqs = [] } = useSelector((s) => s.faq);
  const places = useSelector((s) => s.places?.placesList || []);
  const selectedPlace = useSelector((s) => s.places?.selectedPlace || {});
  const multiSelectedPlaces = useSelector((s) => s.places?.multiSelectedPlaces || []);

  useEffect(() => {
    (async () => {
      if (!places?.length) await dispatch(getPlaces());
      await dispatch(getFaqs());
    })();
  }, [dispatch]);

  useEffect(() => {
    // Sync placeFilter with global dashboard place selector
    if (selectedPlace && selectedPlace._id) setPlaceFilter(selectedPlace._id);
  }, [selectedPlace?._id]);


 const filteredFaqs = useMemo(() => {
  const q = search.toLowerCase();
  return faqs
    .filter(
      (f) =>
        f.question?.toLowerCase().includes(q) ||
        f.answer?.toLowerCase().includes(q)
    )
    .filter((f) => {
      if (!placeFilter) return true;
      // Backend now stores one place per FAQ as placeId
      return f.placeId === placeFilter;
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0) || new Date(b.createdAt) - new Date(a.createdAt));
}, [faqs, search, placeFilter]);

const handleOpen = (row) => {
  setEditing(row || null);
  setForm(
    row
      ? { question: row.question, answer: row.answer, placeIds: row.placeId ? [row.placeId] : [] }
      : { question: "", answer: "", placeIds: [] }
  );
  setPlaceSelectLocked(false);
  setOpen(true);
};
  const handleClose = () => setOpen(false);

  const handleDelete = async (row) => {
    await dispatch(deleteFaq(row._id)).unwrap();
    await dispatch(getFaqs());
  };

  const handleReorder = async (currentIndex, direction) => {
    if (!placeFilter) return; // enforce per-place ordering
    const list = [...filteredFaqs];
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    // swap
    const tmp = list[currentIndex];
    list[currentIndex] = list[targetIndex];
    list[targetIndex] = tmp;
    // normalize orders to index
    const items = list.map((it, idx) => ({ _id: it._id, order: idx }));
    await dispatch(reorderFaqs({ items, placeId: placeFilter })).unwrap();
    await dispatch(getFaqs());
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={1}
                mt={-2}
                py={1}
                px={1}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  FAQ
                </MDTypography>
                
              </MDBox>
            
              <MDBox p={2}>
                <Grid container spacing={2}>
                               <Grid item xs={12} md={6}>
                  <MDBox>
                    <PlacePicker />
                  </MDBox>
                  </Grid>
                  <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: "2px 8px",
                      display: "flex",
                      alignItems: "center",
                      borderRadius: 2,
                      border: "1px solid #eee",
                      boxShadow: "none",
                      background: "transparent",
                    }}
                  >
                    <Icon sx={{ color: "white", mr: 1 }}>search</Icon>
                    <InputBase
                      sx={{ flex: 1, fontSize: 14, color: "white" }}
                      placeholder="Search question/answer..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Paper>
                  </Grid>
                  <Grid item xs={12} md={2}>
                  <MDButton
                    // variant="outlined"
                    size="small"
                    className="me-2"
                    onClick={() => handleOpen()}
                  >
                    Add FAQ
                  </MDButton>
                  </Grid>
              
                  {filteredFaqs.map((row, idx) => (
                    <Grid item xs={12} md={12} key={row._id}>
                      <Card
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <MDTypography variant="h6">
                              {row.question}
                            </MDTypography>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Move up">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!placeFilter || idx === 0}
                                    onClick={() => handleReorder(idx, "up")}
                                  >
                                    <Icon>arrow_upward</Icon>
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!placeFilter || idx === filteredFaqs.length - 1}
                                    onClick={() => handleReorder(idx, "down")}
                                  >
                                    <Icon>arrow_downward</Icon>
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleOpen(row)}>
                                  <Icon>edit</Icon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() => handleDelete(row)}
                                >
                                  <Icon>delete</Icon>
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>

                          <MDTypography variant="body2" color="text">
                            {row.answer}
                          </MDTypography>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            {(row.tags || []).map((t, i) => (
                              <Chip key={i} label={t} size="small" />
                            ))}
                          </Stack>
{/* 
                          <FormControl fullWidth size="small">
                            <InputLabel>Assigned Places</InputLabel>
                            <Select
                              multiple
                              value={row.placeIds || []}
                              input={
                                <OutlinedInput label="Assigned Places" />
                              }
                              renderValue={(selected) =>
                               <MDTypography variant="caption" color="text">
  Place: {places.find((p) => p._id === row.placeId)?.parkingCode || "-"}
</MDTypography>
                              }
                              onChange={(e) =>
                                handleAssignPlaces(row, e.target.value)
                              }
                            >
                              {places.map((p) => (
                                <MenuItem key={p._id} value={p._id}>
                                  <Checkbox
                                    checked={
                                      (row.placeIds || []).indexOf(p._id) > -1
                                    }
                                  />
                                  <ListItemText
                                    primary={`${p.parkingCode} — ${p.lotName}`}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl> */}
                        </Stack>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
  <DialogTitle>{editing ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
  <Formik
    initialValues={form}
    enableReinitialize
    validationSchema={Yup.object().shape({
      question: Yup.string().required("Question is required"),
      answer: Yup.string().required("Answer is required"),
      placeIds: Yup.array().of(Yup.string()).min(1, "Select at least one place"),
    })}
    onSubmit={async (values, { setSubmitting }) => {
      try {
        const payload = {
          question: values.question,
          answer: values.answer,
          placeIds: values.placeIds,
        };
        if (editing?._id) {
          await dispatch(updateFaq({ faqId: editing._id, data: payload })).unwrap();
        } else {
          await dispatch(createFaq(payload)).unwrap();
        }
        await dispatch(getFaqs());
        setOpen(false);
      } finally {
        setSubmitting(false);
      }
    }}
  >
    {({ values, errors, touched, setFieldValue, isSubmitting }) => (
      <Form>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <MDInput
              label="Question"
              fullWidth
              value={values.question}
              onChange={(e) => setFieldValue("question", e.target.value)}
              error={Boolean(touched.question && errors.question)}
              helperText={touched.question && errors.question}
            />
            <MDInput
              label="Answer"
              fullWidth
              multiline
              minRows={4}
              value={values.answer}
              onChange={(e) => setFieldValue("answer", e.target.value)}
              error={Boolean(touched.answer && errors.answer)}
              helperText={touched.answer && errors.answer}
            />

            <Stack spacing={1}>
              <MDTypography variant="subtitle2">Assign Places</MDTypography>
              <Box
                sx={{
                  opacity: placeSelectLocked ? 0.6 : 1,
                  pointerEvents: placeSelectLocked ? "none" : "auto",
                }}
              >
                <MultiPlacePicker />
              </Box>
              {/** Sync MultiPlacePicker (Redux) -> Formik values.placeIds */}
              <FormikPlacesSync
                selectedIds={(multiSelectedPlaces || [])
                  .map((p) => (typeof p === "string" ? p : p?._id))
                  .filter((id) => id && id !== "all")}
              />
              {/* <Stack direction="row" spacing={1}>
                {!placeSelectLocked ? (
                  <MDButton
                    color="info"
                    variant="outlined"
                    onClick={() => setPlaceSelectLocked(true)}
                  >
                    OK
                  </MDButton>
                ) : (
                  <MDButton
                    color="secondary"
                    variant="text"
                    onClick={() => setPlaceSelectLocked(false)}
                  >
                    Change
                  </MDButton>
                )}
              </Stack>

              <FormControl fullWidth size="small">
                <InputLabel>Places</InputLabel>
                <Select
                  multiple
                  value={values.placeIds}
                  input={<OutlinedInput label="Places" />}
                  renderValue={(selected) =>
                    selected
                      .map((id) => places.find((p) => p._id === id)?.parkingCode || "")
                      .filter(Boolean)
                      .join(", ")
                  }
                  onChange={(e) => setFieldValue("placeIds", e.target.value)}
                >
                  {places.map((p) => (
                    <MenuItem key={p._id} value={p._id}>
                      <Checkbox checked={values.placeIds.indexOf(p._id) > -1} />
                      <ListItemText primary={`${p.parkingCode} — ${p.lotName}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
              {touched.placeIds && errors.placeIds && (
                <MDTypography variant="caption" color="error">
                  {errors.placeIds}
                </MDTypography>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </MDButton>
          <MDButton color="info" type="submit" disabled={isSubmitting}>
            {editing ? "Update" : "Create"}
          </MDButton>
        </DialogActions>
      </Form>
    )}
  </Formik>
</MDDialog>

      <Footer />
    </DashboardLayout>
  );
}